// catalog.state.ts
import { AxiosInstance } from 'axios';
import { defineStore } from 'pinia';

import { CatalogsApi } from 'SharedDir/backend/api';
import { Catalogs } from 'SharedDir/declarations/catalogs';

/**
 * Типизация состояния хранилища.
 */
export interface CatalogsStateInterface {
  // Храним каталоги по slug, если каталог не загружен — значение null
  itemsBySlug: Record<string, Catalogs.CatalogsFull | null>;
  // Храним активные запросы (промисы) по slug
  requestsInFlight: Record<string, Promise<Catalogs.CatalogsFull> | null>;
}

/**
 * Pinia‑store для работы с каталогами.
 */
export const useCatalogStore = defineStore('catalog', {
  state: (): CatalogsStateInterface => ({
    itemsBySlug: {},
    requestsInFlight: {},
  }),
  getters: {
    /**
     * Геттер для получения каталога по slug.
     */
    getCatalogBySlug: (state) => {
      return (slug: string): Catalogs.CatalogsFull | null => {
        return state.itemsBySlug[slug] || null;
      };
    },
    /**
     * Геттер для получения значений каталога по slug.
     */
    getCatalogValuesBySlug: (state) => {
      return (slug: string): Array<Catalogs.CatalogsValueFull> => {
        return state.itemsBySlug[slug]?.values || [];
      };
    },
  },
  actions: {
    /**
     * Асинхронный экшн для получения каталога по slug.
     *
     * Алгоритм:
     * 1. Если каталог уже загружен (itemsBySlug), сразу возвращаем его.
     * 2. Если уже идёт запрос (requestsInFlight) — возвращаем его.
     * 3. Иначе делаем запрос через API, сохраняем промис в state (чтобы на повторный вызов вернуть его),
     *    а по завершении сохраняем результат в state и очищаем промис.
     */
    async fetchCatalogBySlug({
      slug,
    }: {
      slug: Catalogs.CatalogSlug;
    }): Promise<Catalogs.CatalogsFull> {
      // 1. Если каталог уже загружен — возвращаем его.
      if (this.itemsBySlug[slug]) {
        return this.itemsBySlug[slug] as Catalogs.CatalogsFull;
      }

      // 2. Если есть уже активный запрос — возвращаем его.
      if (this.requestsInFlight[slug]) {
        return this.requestsInFlight[slug] as Promise<Catalogs.CatalogsFull>;
      }

      // 3. Выполняем запрос и сохраняем промис в состоянии.
      const requestPromise = CatalogsApi.ValuesApi(slug)
        .getAllValues()
        .then((catalog: Catalogs.CatalogsFull) => {
          // По завершении сохраняем каталог и очищаем промис.
          this.setCatalog({ slug, catalog });
          this.setRequestInFlight({ slug, request: null });
          return catalog;
        })
        .catch((error) => {
          // При ошибке также очищаем промис.
          this.setRequestInFlight({ slug, request: null });
          throw error;
        });

      // Сохраняем промис, чтобы повторный вызов fetchCatalogBySlug для того же slug вернул тот же промис.
      this.setRequestInFlight({ slug, request: requestPromise });

      return requestPromise;
    },
    /**
     * Устанавливает каталог в состоянии.
     */
    setCatalog({ slug, catalog }: { slug: string; catalog: Catalogs.CatalogsFull }) {
      this.itemsBySlug[slug] = catalog;
    },
    /**
     * Устанавливает или очищает промис активного запроса для указанного slug.
     */
    setRequestInFlight({
      slug,
      request,
    }: {
      slug: string;
      request: Promise<Catalogs.CatalogsFull> | null;
    }) {
      this.requestsInFlight[slug] = request;
    },
  },
});
